import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  CheckCircle2,
  Receipt,
  LoaderCircle,
  AlertCircle,
  ShoppingBag,
  Image as ImageIcon,
  Type,
  Package,
} from "lucide-react";
import { useApi } from "../utils/Api";
import { useCart } from "../context/CartContext";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

function getUploadImage(upload) {
  const image = upload?.file_url || upload?.file_path || "";

  if (!image) return "";

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  return `${process.env.REACT_APP_UPLOADS_BASE_URL || "https://kinewsstudiolimited.co.uk"}/${image.replace(/^\/+/, "")}`;
}

export default function ShopOrderSuccess() {
  const { apiFetch } = useApi();
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const hasClearedCartRef = useRef(false);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setError("Missing payment session.");
      setLoading(false);
      return;
    }

    let intervalId;

    const loadOrder = async () => {
      try {
        const res = await apiFetch("verify_shop_order_payment", {
          session_id: sessionId,
        });

        if (!res?.success || !res?.data) {
          throw new Error(res?.message || "Unable to load order details.");
        }

        setOrder(res.data);
        setError("");

        if (res.data.payment_status === "paid" && !hasClearedCartRef.current) {
          clearCart();
          hasClearedCartRef.current = true;
        }

        if (res.data.payment_status === "paid" && intervalId) {
          clearInterval(intervalId);
        }
      } catch (err) {
        setError(err.message || "Unable to load order details.");
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
    intervalId = setInterval(loadOrder, 3000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [sessionId, apiFetch, clearCart]);

  const itemCount = useMemo(() => {
    if (!order?.items || !Array.isArray(order.items)) return 0;
    return order.items.reduce((sum, item) => sum + Number(item.qty || 0), 0);
  }, [order]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#1c1c1c] px-4 py-10 pt-28 sm:px-6">
        <div className="mx-auto max-w-[560px] rounded-2xl bg-white px-6 py-14 text-center shadow-sm sm:px-10">
          <div className="flex justify-center">
            <LoaderCircle className="h-12 w-12 animate-spin text-black" />
          </div>
          <p className="mt-5 text-base text-[#333]">Loading order details...</p>
        </div>
      </main>
    );
  }

  if (error || !order) {
    return (
      <main className="min-h-screen bg-[#1c1c1c] px-4 py-10 pt-28 sm:px-6">
        <div className="mx-auto max-w-[560px] rounded-2xl bg-white px-6 py-14 text-center shadow-sm sm:px-10">
          <div className="flex justify-center">
            <AlertCircle className="h-12 w-12 text-red-500" />
          </div>

          <h1 className="mt-5 text-2xl font-semibold text-[#111]">
            Unable to load order
          </h1>

          <p className="mt-3 text-base text-[#444]">
            {error || "Something went wrong while loading your order details."}
          </p>

          <Link
            to="/shop"
            className="mt-8 inline-flex rounded-xl bg-pink-500 px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90"
          >
            Back to shop
          </Link>
        </div>
      </main>
    );
  }

  if (order.payment_status !== "paid") {
    return (
      <main className="min-h-screen bg-[#1c1c1c] px-4 py-10 pt-28 sm:px-6">
        <div className="mx-auto max-w-[560px] rounded-2xl bg-white px-6 py-14 text-center shadow-sm sm:px-10">
          <div className="flex justify-center">
            <LoaderCircle className="h-12 w-12 animate-spin text-black" />
          </div>

          <h1 className="mt-6 text-2xl font-semibold text-[#111]">
            Processing your payment...
          </h1>

          <p className="mt-3 text-base text-[#444]">
            Please wait while we confirm your order payment.
          </p>

          {order.order_number && (
            <p className="mt-4 text-sm text-[#666]">
              Order number:{" "}
              <span className="font-medium text-[#222]">
                {order.order_number}
              </span>
            </p>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#1c1c1c] px-4 py-8 pt-28 sm:px-6">
      <div className="mx-auto max-w-[560px] overflow-hidden rounded-2xl bg-white shadow-sm">
        <div className="px-6 pb-8 pt-10 text-center sm:px-10">
          <div className="flex justify-center">
            <CheckCircle2 size={62} strokeWidth={1.8} className="text-black" />
          </div>

          <h1 className="mt-5 text-3xl font-semibold tracking-tight text-[#1c1c1c]">
            Thanks for your order!
          </h1>

          <p className="mx-auto mt-4 max-w-[380px] text-lg leading-9 text-[#2b2b2b]">
            Your payment was successful and your order is now confirmed.
          </p>

          {order.order_number && (
            <p className="mt-3 text-sm text-[#666]">
              Order number:{" "}
              <span className="font-medium text-[#222]">
                {order.order_number}
              </span>
            </p>
          )}
        </div>

        <div className="px-6 py-8 sm:px-10">
          <h2 className="text-[21px] font-medium text-[#222222]">
            Order Summary
          </h2>

          <div className="mt-5 flex items-center gap-4">
            <ShoppingBag size={24} strokeWidth={1.9} className="text-black" />
            <p className="text-[17px] leading-7 text-[#222222]">
              {itemCount} item{itemCount > 1 ? "s" : ""} in this order
            </p>
          </div>

          <div className="mt-7 space-y-5">
            {(order.items || []).map((item) => (
              <div
                key={item.id}
                className="rounded-2xl border border-black/10 p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-[17px] font-bold leading-7 text-[#222222]">
                      {item.product_name}
                    </p>

                    {(item.options || []).length > 0 && (
                      <div className="mt-3 space-y-2">
                        {(item.options || []).map((option, index) => (
                          <div
                            key={`${item.id}_opt_${index}`}
                            className="flex items-center gap-3"
                          >
                            <Package
                              size={18}
                              strokeWidth={1.9}
                              className="text-black"
                            />
                            <p className="text-[15px] leading-7 text-[#222222]">
                              {option.option_label}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {item.custom_text && (
                      <div className="mt-3 flex items-start gap-3">
                        <Type
                          size={18}
                          strokeWidth={1.9}
                          className="mt-1 text-black"
                        />
                        <div>
                          <p className="text-xs uppercase tracking-[0.15em] text-[#666]">
                            Custom text
                          </p>
                          <p className="mt-1 text-[15px] leading-7 text-[#222222] whitespace-pre-wrap">
                            {item.custom_text}
                          </p>
                        </div>
                      </div>
                    )}

                    {(item.uploads || []).length > 0 && (
                      <div className="mt-3">
                        <div className="mb-2 flex items-center gap-3">
                          <ImageIcon
                            size={18}
                            strokeWidth={1.9}
                            className="text-black"
                          />
                          <p className="text-[15px] leading-7 text-[#222222]">
                            Uploaded image
                            {item.uploads.length > 1 ? "s" : ""}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {(item.uploads || []).map((upload, index) => {
                            const imageSrc = getUploadImage(upload);

                            return (
                              <div
                                key={`${item.id}_upload_${index}`}
                                className="h-14 w-14 overflow-hidden rounded-xl border border-black/10 bg-black/[0.03]"
                              >
                                {imageSrc ? (
                                  <img
                                    src={imageSrc}
                                    alt={
                                      upload.original_name ||
                                      `Upload ${index + 1}`
                                    }
                                    className="h-full w-full object-cover"
                                  />
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="text-right">
                    <p className="text-[15px] text-[#666]">Qty: {item.qty}</p>
                    <p className="mt-1 text-[17px] font-semibold leading-7 text-[#222222]">
                      {money(item.line_total)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <h2 className="text-[21px] font-medium text-[#222222]">Payment</h2>

            <div className="mt-5 flex items-start gap-4">
              <Receipt
                size={24}
                strokeWidth={1.9}
                className="mt-1 text-black"
              />
              <p className="text-[17px] leading-7 text-[#222222]">
                <span className="font-medium">{money(order.total)}</span> was
                successfully paid.
              </p>
            </div>

            {order.payment_status && (
              <p className="mt-4 text-sm text-[#666]">
                Status:{" "}
                <span className="font-medium capitalize text-[#222]">
                  {order.payment_status}
                </span>
              </p>
            )}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/shop"
              className="inline-flex rounded-xl bg-pink-500 px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90"
            >
              Continue shopping
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
